// matchmaking.jsx
"use client";

import React, { useEffect, useState, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { Modal } from "react-bootstrap";
import Head from "../Base/Head";
import { motion } from "framer-motion";
import Footer from "../Base/Footer";
import ApiService from "../services/ApiService";
import { toast } from "react-hot-toast";
import { AddToCalendarButton } from "add-to-calendar-button-react";

import { generateICSFile, isPersonalEmail } from "../utils";
import ApolloQuote from "./components/ApolloQuote";

import Navbar from "./components/Navbar";

const ALL_GEOGRAPHY_OPTIONS = [
  "New York",
  "Greenwich",
  "El Segundo/LA",
  "North America",
  "International",
];

const ALL_SECTOR_OPTIONS = [
  "Data",
  "Finance / Accounting",
  "Fundraising",
  "Investment Management",
  "Legal / Compliance",
  "Marketing / Communications",
  "Operations",
  "Real Estate",
  "Risk management",
  "Strategy",
  "Talent / Human Capital",
  "Technology / Engineering",
  "Other",
];

// Compact hero (updated with nonprofit CTA link)
const MatchmakingHeroCompact = () => {
  return (
    <section className="portal-hero portal-hero--compact bg-9">
      <div className="portal-hero__overlay" />
      <div className="portal-hero__content">
        <h1 className="portal-hero__title">Apollo Purpose Portal</h1>
        <p className="portal-hero__subtitle">
          Explore curated non-profit board opportunities aligned with your interests, experience, and impact goals.
        </p>

        {/* Nonprofit CTA */}
        <p className="portal-hero__cta">
          Non-profit eager to join the Apollo Purpose Portal?{" "}
          <a className="portal-hero__cta-link" href="/nonprofit-signup">
            CLICK HERE (Organization)
          </a>
        </p>
        {/* Employee CTA */}
        <p className="portal-hero__cta">
          Apollo employee interested to join a non-profit board?{" "}
          <a
            className="portal-hero__cta-link portal-hero__cta-link--employee"
            href="/employee-signup"
          >
            CLICK HERE (Employee)
          </a>
        </p>
      </div>
    </section>
  );
};

/* ---------- Modal for nonprofit details ---------- */
const NonprofitModal = ({ org, onClose }) => {
  if (!org) return null;

  const handleKeyDown = (e) => {
    if (e.key === "Escape") onClose();
  };

  return (
    <div className="portal-modal" onKeyDown={handleKeyDown}>
      <div className="portal-modal__backdrop" onClick={onClose} />
      <div className="portal-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="portal-modal-title">
        <button type="button" className="portal-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="portal-modal__header">
          <h2 id="portal-modal-title" className="portal-modal__title">
            {org.name}
          </h2>
          <p className="portal-modal__tagline">{org.tagline}</p>
        </header>

        <div className="portal-modal__meta">
          <span className="nonprofit-card__chip nonprofit-card__chip--geo">{org.geography}</span>
          <span className="nonprofit-card__chip nonprofit-card__chip--sector">{org.sector}</span>
        </div>

        <p className="portal-modal__description">{org.description}</p>

        <ul className="portal-modal__list">
          <li>
            <strong>HQ:</strong> {org.location}
          </li>
          <li>
            <strong>Board Need:</strong> {org.boardNeed}
          </li>
          <li>
            <strong>Ideal Profile:</strong> {org.idealProfile || "Not specified"}
          </li>
        </ul>

        <div className="portal-modal__footer">
          <a className="portal-modal__cta" href={`mailto:${org.contactEmail || "info@example.org"}`}>
            Contact Organization
          </a>
        </div>
      </div>
    </div>
  );
};

/* ---------- Filters (unchanged) ---------- */
const NonprofitFilters = ({
  geographyFilter,
  sectorFilter,
  onGeographyChange,
  onSectorChange,
  geographyOptions,
  sectorOptions,
}) => {
  return (
    <div className="portal-filters">
      <div className="portal-filters__group">
        <label className="portal-filters__label" htmlFor="geography-filter">
          Geography
        </label>
        <select
          id="geography-filter"
          className="portal-filters__select"
          value={geographyFilter}
          onChange={(e) => onGeographyChange(e.target.value)}
        >
          <option value="all">All Geographies</option>
          {geographyOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="portal-filters__group">
        <label className="portal-filters__label" htmlFor="sector-filter">
          Sector
        </label>
        <select
          id="sector-filter"
          className="portal-filters__select"
          value={sectorFilter}
          onChange={(e) => onSectorChange(e.target.value)}
        >
          <option value="all">All Sectors</option>
          {sectorOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

/* ---------- Small form helpers (mirrors NonProfitSignUp.jsx patterns) ---------- */
const CheckboxRow = ({ id, label, checked, disabled, onChange, className = "" }) => {
  return (
    <label htmlFor={id} className={`exec-interest__choice ${disabled ? "is-disabled" : ""} ${className}`}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="exec-interest__choice-label">{label}</span>
    </label>
  );
};

const RadioRow = ({ id, name, label, checked, onChange }) => {
  return (
    <label htmlFor={id} className="exec-interest__choice">
      <input id={id} type="radio" name={name} checked={checked} onChange={onChange} />
      <span className="exec-interest__choice-label">{label}</span>
    </label>
  );
};

/* ---------- Card (no modal, upgraded design) ---------- */
const NonprofitCard = ({ org, isSelected, onToggleSelected, selectionDisabled }) => {
  const stageLabel = org.stage || "Scaling";

  const focusPills = org.focusAreas?.length ? org.focusAreas.slice(0, 2) : org.sector ? [org.sector] : [];

  return (
    <article className="np-card">
      {/* Top gradient bar */}
      <header className="np-card__top">
        <div className="np-card__top-left">
          <div className="np-card__logo" aria-hidden="true">
            {org.logo ? (
              <img className="np-card__logo-img" src={org.logo} alt="" loading="lazy" />
            ) : (
              <div className="np-card__logo-fallback">
                <span className="np-card__logo-fallback-text">
                  {org.name?.slice(0, 1)?.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          <div className="np-card__title-wrap">
            <h3 className="np-card__name">{org.name}</h3>
            <p className="np-card__tagline">{org.tagline}</p>
          </div>
        </div>

        <div className="np-card__stage" title="Organization stage">
          {stageLabel}
        </div>
      </header>

      {/* Body */}
      <div className="np-card__body">
        <div className="np-card__block np-card__block--mission">
          <div className="np-card__label">Mission</div>
          <p className="np-card__mission">{org.description}</p>
        </div>

        <div className="np-card__meta">
          <div className="np-card__meta-item">
            <div className="np-card__meta-label">Region</div>
            <div className="np-card__meta-value">{org.geography}</div>
          </div>

          <div className="np-card__meta-item">
            <div className="np-card__meta-label">Focus</div>
            <div className="np-card__pills">
              {focusPills.length ? (
                focusPills.map((t) => (
                  <span className="np-card__pill" key={t}>
                    {t}
                  </span>
                ))
              ) : (
                <span className="np-card__muted">Not specified</span>
              )}
            </div>
          </div>

          <div className="np-card__meta-item np-card__meta-item--website">
            <div className="np-card__meta-label">Website</div>
            {org.website ? (
              <a
                className="np-card__link"
                href={org.website}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {org.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                <span className="np-card__link-icon" aria-hidden="true">
                  ↗
                </span>
              </a>
            ) : (
              <span className="np-card__muted">Not provided</span>
            )}
          </div>
        </div>

        <div className="np-card__block">
          <div className="np-card__label">Board Need</div>
          <p className="np-card__ideal">{org.boardNeed}</p>
        </div>

        <div className="np-card__block np-card__block--ideal">
          <div className="np-card__label">Ideal Profile</div>
          <p className="np-card__ideal">{org.idealProfile || "Not specified"}</p>
        </div>
      </div>

      {/* Footer action (now synced to form selection) */}
      <footer className="np-card__footer">
        <label className="np-card__interest">
          <input
            type="checkbox"
            checked={!!isSelected}
            disabled={selectionDisabled && !isSelected}
            onChange={(e) => onToggleSelected(org.id, e.target.checked)}
          />
          <span className="np-card__interest-text">Interested</span>
          <span className="np-card__interest-subtext">Add to your shortlist</span>
        </label>

        {/* <div className="np-card__confidence">Curated opportunity</div> */}
      </footer>
    </article>
  );
};

/* ---------- Main page ---------- */
const BoardMatchmakingPortalPage = () => {
  const formRef = useRef(null);
  const successNoticeRef = useRef(null);
  const blurTimerRef = useRef(null);
  const pointerDownSectionRef = useRef(null);

  const [geographyFilter, setGeographyFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [nonprofits, setNonprofits] = useState([]);
  const [isLoadingNonprofits, setIsLoadingNonprofits] = useState(true);

  useEffect(() => {
    let active = true;
    const loadNonprofits = async () => {
      setIsLoadingNonprofits(true);
      try {
        const api = new ApiService();
        const response = await api.get("landmark/apollo/nonprofitProfiles");
        if (!active) return;
        if (!response || response.error || !response.success) {
          setNonprofits([]);
          return;
        }
        setNonprofits(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (!active) return;
        setNonprofits([]);
      } finally {
        if (active) setIsLoadingNonprofits(false);
      }
    };

    loadNonprofits();
    return () => {
      active = false;
    };
  }, []);

  const geographyOptions = useMemo(() => ALL_GEOGRAPHY_OPTIONS, []);
  const sectorOptions = useMemo(() => ALL_SECTOR_OPTIONS, []);

  const filteredNonprofits = useMemo(() => {
    return nonprofits.filter((org) => {
      const geographyMatches = geographyFilter === "all" || org.geography === geographyFilter;
      const sectorMatches = sectorFilter === "all" || org.sector === sectorFilter;
      return geographyMatches && sectorMatches;
    });
  }, [nonprofits, geographyFilter, sectorFilter]);

  /* -------------------------
     Leader Expression of Interest form state
  ------------------------- */
  const MAX_ORG_SELECT = 2;
  const MAX_VALUE_ADD = 2;

  const [execName, setExecName] = useState("");
  const [execEmail, setExecEmail] = useState("");

  // interest selection (synced with card checkboxes)
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);

  // role preference (single select)
  const [rolePref, setRolePref] = useState("");

  // value add (multi select, up to 2)
  const [valueAdds, setValueAdds] = useState([]);

  // optional note
  const [note, setNote] = useState("");

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgLimitMsg, setOrgLimitMsg] = useState("");
  const [valueAddLimitMsg, setValueAddLimitMsg] = useState("");

  const valueAddOptions = useMemo(
    () => ["Finance / investing", "Strategy & growth", "Technology / digital", "Governance / risk", "Global experience", "Network access"],
    []
  );

  const emailLooksValid = (val) => {
    const v = `${val}`.trim();
    if (!v) return false;
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v);
  };

  const toggleSelectedOrg = (id, checked) => {
    setOrgLimitMsg("");
    setSelectedOrgIds((prev) => {
      const already = prev.includes(id);

      if (checked) {
        if (already) return prev;
        if (prev.length >= MAX_ORG_SELECT) {
          setOrgLimitMsg(`Please select up to ${MAX_ORG_SELECT}.`);
          return prev;
        }
        return [...prev, id];
      }

      return prev.filter((x) => x !== id);
    });
  };

  const setValueAddChecked = (label, checked) => {
    setValueAddLimitMsg("");
    setValueAdds((prev) => {
      const already = prev.includes(label);

      if (checked) {
        if (already) return prev;
        if (prev.length >= MAX_VALUE_ADD) {
          setValueAddLimitMsg(`Please select up to ${MAX_VALUE_ADD}.`);
          return prev;
        }
        return [...prev, label];
      }

      return prev.filter((x) => x !== label);
    });
  };

  const selectedOrgNames = useMemo(() => {
    const idSet = new Set(selectedOrgIds);
    return nonprofits.filter((n) => idSet.has(n.id)).map((n) => n.name);
  }, [selectedOrgIds, nonprofits]);

  const execFormValid =
    execName.trim().length > 0 &&
    emailLooksValid(execEmail) &&
    selectedOrgIds.length > 0 &&
    selectedOrgIds.length <= MAX_ORG_SELECT &&
    rolePref.length > 0 &&
    valueAdds.length > 0 &&
    valueAdds.length <= MAX_VALUE_ADD;

  const handleExecSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (!execFormValid) return;
    setSubmitted(false);

    const form = {
      name: execName.trim(),
      apolloEmail: execEmail.trim(),
      selectedOrgIds: selectedOrgIds.map((id) => `${id}`),
      selectedOrgNames,
      rolePreference: rolePref,
      valueAdds,
      note: note.trim(),
    };

    try {
      setIsSubmitting(true);
      const api = new ApiService();
      const response = await api.post("landmark/apollo/submitExpressionOfInterest", {
        form,
      });
      if (!response || response.error || !response.success) {
        setSubmitted(false);
        toast.error(response?.error || "Could not submit form. Please try again.");
        return;
      }
      setSubmitted(true);
      window.setTimeout(() => {
        if (!successNoticeRef.current) return;
        const y = window.pageYOffset + successNoticeRef.current.getBoundingClientRect().top - 12;
        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
      }, 60);
    } catch (error) {
      setSubmitted(false);
      toast.error("Could not submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateActiveSectionClass = (activeSection) => {
    const formEl = formRef.current;
    if (!formEl) return;

    formEl
      .querySelectorAll(".exec-interest__section.is-focus-active")
      .forEach((sectionEl) => {
        if (sectionEl !== activeSection) sectionEl.classList.remove("is-focus-active");
      });

    if (activeSection) activeSection.classList.add("is-focus-active");
  };

  const handleFormFocusCapture = (e) => {
    const sectionEl = e.target.closest?.(".exec-interest__section");
    if (!sectionEl) return;

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    updateActiveSectionClass(sectionEl);
  };

  const handleFormMouseDownCapture = (e) => {
    const sectionEl = e.target.closest?.(".exec-interest__section");
    if (!sectionEl) return;
    pointerDownSectionRef.current = sectionEl;
    updateActiveSectionClass(sectionEl);
  };

  const handleFormBlurCapture = (e) => {
    const sourceSection = e.target.closest?.(".exec-interest__section");
    const nextSection = e.relatedTarget?.closest?.(".exec-interest__section") || null;

    if (sourceSection && nextSection && sourceSection === nextSection) {
      updateActiveSectionClass(sourceSection);
      return;
    }

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
    }

    blurTimerRef.current = window.setTimeout(() => {
      const activeElement = document.activeElement;
      const activeSection = activeElement?.closest?.(".exec-interest__section") || null;
      if (activeSection) {
        updateActiveSectionClass(activeSection);
      } else if (pointerDownSectionRef.current) {
        updateActiveSectionClass(pointerDownSectionRef.current);
      } else {
        updateActiveSectionClass(null);
      }
      pointerDownSectionRef.current = null;
      blurTimerRef.current = null;
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const pageStaggerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.08,
        delayChildren: 0.03,
      },
    },
  };

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <motion.div className="portal-page" initial="hidden" animate="show" variants={pageStaggerVariants}>
      <motion.div variants={fadeUpVariants}>
        <MatchmakingHeroCompact />
      </motion.div>

      <motion.header className="portal-page__header" variants={fadeUpVariants}>
        <h2 className="portal-page__heading">Explore Board Opportunities</h2>
        <p className="portal-page__intro">
          Browse curated non-profit partners actively seeking senior leaders for their boards. Use the filters below to refine by geography and sector.
        </p>
      </motion.header>

      <motion.main className="portal-page__main" variants={fadeUpVariants}>
        <NonprofitFilters
          geographyFilter={geographyFilter}
          sectorFilter={sectorFilter}
          onGeographyChange={setGeographyFilter}
          onSectorChange={setSectorFilter}
          geographyOptions={geographyOptions}
          sectorOptions={sectorOptions}
        />

        <section className="portal-results">
          {isLoadingNonprofits ? (
            <p className="portal-results__empty">Loading non-profit opportunities...</p>
          ) : filteredNonprofits.length === 0 ? (
            <p className="portal-results__empty">No organizations match your current filters. Try broadening your geography or sector.</p>
          ) : (
            <div className="portal-results__grid">
              {filteredNonprofits.map((org) => (
                <NonprofitCard
                  key={org.id}
                  org={org}
                  isSelected={selectedOrgIds.includes(org.id)}
                  selectionDisabled={selectedOrgIds.length >= MAX_ORG_SELECT}
                  onToggleSelected={toggleSelectedOrg}
                />
              ))}
            </div>
          )}
        </section>

        {/* -------- Leader Expression of Interest Form (beneath cards) -------- */}
        <section className="exec-interest">
          <div className="exec-interest__rail">
            <header className="exec-interest__header">
              <h3 className="exec-interest__title">Leader Interest</h3>
              <p className="exec-interest__subtitle">
                Review the non-profits above and indicate where you’d like to explore a potential board or advisory role.
              </p>
            </header>

            {submitted ? (
              <div
                ref={successNoticeRef}
                className="exec-interest__notice exec-interest__notice--success-blue"
                role="status"
              >
                <div className="exec-interest__notice-title">Thank you</div>
                <div className="exec-interest__notice-text">
                  Your expression of interest has been submitted. The Apollo Opportunity team will follow up with next steps if there&apos;s a potential fit.
                </div>
              </div>
            ) : null}

            <form
              ref={formRef}
              className="exec-interest__card"
              onSubmit={handleExecSubmit}
              onFocusCapture={handleFormFocusCapture}
              onBlurCapture={handleFormBlurCapture}
              onMouseDownCapture={handleFormMouseDownCapture}
              onTouchStartCapture={handleFormMouseDownCapture}
            >
              {/* 1. Basic Info */}
              <section className="exec-interest__section">
                <div className="exec-interest__section-top">
                  <div className="exec-interest__step">
                    <span className="exec-interest__step-number">1</span>
                  </div>
                  <div>
                    <div className="exec-interest__kicker">Basic info</div>
                    <div className="exec-interest__subkicker">Internal-only. No title or bio needed.</div>
                  </div>
                </div>

                <div className="exec-interest__grid">
                  <div className="exec-interest__field">
                    <div className="exec-interest__label">
                      Name <span className="exec-interest__req">*</span>
                    </div>
                    <input
                      className="exec-interest__input"
                      value={execName}
                      onChange={(e) => setExecName(e.target.value)}
                      placeholder="Full name"
                    />
                    {submitAttempted && !execName.trim().length ? (
                      <div className="exec-interest__error">Please enter your name.</div>
                    ) : null}
                  </div>

                  <div className="exec-interest__field">
                    <div className="exec-interest__label">
                      Work email <span className="exec-interest__req">*</span>
                    </div>
                    <input
                      className="exec-interest__input"
                      type="email"
                      value={execEmail}
                      onChange={(e) => setExecEmail(e.target.value)}
                      placeholder="name@apollo.com"
                    />
                    {submitAttempted && !emailLooksValid(execEmail) ? (
                      <div className="exec-interest__error">Please enter a valid email address.</div>
                    ) : null}
                  </div>
                </div>
              </section>

              {/* 2. Interest Selection */}
              <section className="exec-interest__section">
                <div className="exec-interest__section-top">
                  <div className="exec-interest__step">
                    <span className="exec-interest__step-number">2</span>
                  </div>
                  <div>
                    <div className="exec-interest__kicker">Interest selection</div>
                    <div className="exec-interest__subkicker">Select up to {MAX_ORG_SELECT}.</div>
                  </div>
                </div>

                <div className="exec-interest__label">
                  Which organization(s) would you like to explore further? <span className="exec-interest__req">*</span>
                </div>

                <div className="exec-interest__choices-grid">
                  {filteredNonprofits.map((org) => {
                    const id = `org-select-${org.id}`;
                    const checked = selectedOrgIds.includes(org.id);
                    const disableNew = !checked && selectedOrgIds.length >= MAX_ORG_SELECT;

                    return (
                      <CheckboxRow
                        key={org.id}
                        id={id}
                        label={org.name}
                        checked={checked}
                        disabled={disableNew}
                        onChange={(checkedNow) => toggleSelectedOrg(org.id, checkedNow)}
                      />
                    );
                  })}
                </div>

                {orgLimitMsg ? <div className="exec-interest__error">{orgLimitMsg}</div> : null}

                {submitAttempted && selectedOrgIds.length === 0 ? (
                  <div className="exec-interest__error">Please select at least one organization.</div>
                ) : null}
              </section>

              {/* 3. Role preference */}
              <section className="exec-interest__section">
                <div className="exec-interest__section-top">
                  <div className="exec-interest__step">
                    <span className="exec-interest__step-number">3</span>
                  </div>
                  <div>
                    <div className="exec-interest__kicker">Role preference</div>
                    <div className="exec-interest__subkicker">Helps us route follow-up appropriately.</div>
                  </div>
                </div>

                <div className="exec-interest__label">
                  What type of involvement are you open to? <span className="exec-interest__req">*</span>
                </div>

                <div className="exec-interest__choices-stack">
                  <RadioRow
                    id="role-board"
                    name="rolePref"
                    label="Board member"
                    checked={rolePref === "board"}
                    onChange={() => setRolePref("board")}
                  />
                  <RadioRow
                    id="role-advisory"
                    name="rolePref"
                    label="Advisory role"
                    checked={rolePref === "advisory"}
                    onChange={() => setRolePref("advisory")}
                  />
                  <RadioRow
                    id="role-either"
                    name="rolePref"
                    label="Open to either"
                    checked={rolePref === "either"}
                    onChange={() => setRolePref("either")}
                  />
                </div>

                {submitAttempted && !rolePref.length ? <div className="exec-interest__error">Please select one option.</div> : null}
              </section>

              {/* 4. Value add */}
              <section className="exec-interest__section">
                <div className="exec-interest__section-top">
                  <div className="exec-interest__step">
                    <span className="exec-interest__step-number">4</span>
                  </div>
                  <div>
                    <div className="exec-interest__kicker">Value add</div>
                    <div className="exec-interest__subkicker">Select up to {MAX_VALUE_ADD}.</div>
                  </div>
                </div>

                <div className="exec-interest__label">
                  Where do you think you could add the most value? <span className="exec-interest__req">*</span>
                </div>

                <div className="exec-interest__choices-grid">
                  {valueAddOptions.map((opt) => {
                    const id = `valueadd-${opt.replace(/[^\w]+/g, "-").toLowerCase()}`;
                    const checked = valueAdds.includes(opt);
                    const disableNew = !checked && valueAdds.length >= MAX_VALUE_ADD;

                    return (
                      <CheckboxRow
                        key={opt}
                        id={id}
                        label={opt}
                        checked={checked}
                        disabled={disableNew}
                        onChange={(checkedNow) => setValueAddChecked(opt, checkedNow)}
                      />
                    );
                  })}
                </div>

                {valueAddLimitMsg ? <div className="exec-interest__error">{valueAddLimitMsg}</div> : null}

                {submitAttempted && valueAdds.length === 0 ? (
                  <div className="exec-interest__error">Please select at least one value area.</div>
                ) : null}
              </section>

              {/* 5. Optional note */}
              <section className="exec-interest__section exec-interest__section--last">
                <div className="exec-interest__section-top">
                  <div className="exec-interest__step">
                    <span className="exec-interest__step-number">5</span>
                  </div>
                  <div>
                    <div className="exec-interest__kicker">Optional note</div>
                    <div className="exec-interest__subkicker">One line is plenty.</div>
                  </div>
                </div>

                <div className="exec-interest__label">Anything else we should know?</div>
                <input
                  className="exec-interest__input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional"
                />
              </section>

              <div className="exec-interest__actions">
                <button type="submit" className="exec-interest__submit" disabled={isSubmitting || (!execFormValid && submitAttempted)}>
                  {isSubmitting ? "Submitting..." : "Express interest"}
                </button>

                <div className="exec-interest__subtext">This does not represent a formal commitment.</div>

                {submitAttempted && !execFormValid ? (
                  <div className="exec-interest__error exec-interest__error--center">Please complete the required fields above.</div>
                ) : (
                  <div className="exec-interest__error-spacer" />
                )}
              </div>
            </form>
          </div>
        </section>
      </motion.main>

      {/* Modal */}
      <NonprofitModal org={selectedOrg} onClose={() => setSelectedOrg(null)} />
    </motion.div>
  );
};

const customFieldLabels = {
  "glean-sydney": { Email: "Official Email" },
};

const RsvpField = ({
  className = "",
  fieldLabel,
  inputType = "text",
  value,
  setValue,
  required,
  onBlur = () => {},
}) => {
  const [isValid, setIsValid] = useState(false);
  useEffect(() => {
    if (!required) {
      setIsValid(true);
    } else if (required && `${value}`.trim().length) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [value]);

  return (
    <div className={`rsvp-field ${className}`}>
      <div className="rsvp-field-label">
        {fieldLabel}
        {required ? <span className="asterisk">*</span> : null}
      </div>
      {inputType !== "textarea" ? (
        <input type={inputType} className="form-control" value={value} onChange={(event) => setValue(event.target.value)} onBlur={onBlur} />
      ) : (
        <textarea rows="2" className="form-control" value={value} onChange={(event) => setValue(event.target.value)} onBlur={onBlur}></textarea>
      )}
    </div>
  );
};

export default function DynamicEvent({ event = {}, referral = {} }) {
  const portalTitle = "Apollo Purpose Portal - Apollo Opportunity Foundation";
  const { pageContent, registrationClosed, calendarOptions } = event;

  const textClass = pageContent?.textClass || "";
  const headerTextClass = pageContent?.headerTextClass || "";

  useEffect(() => {
    document.title = portalTitle;
  }, [portalTitle]);

  const head = {
    title: portalTitle,
    description: pageContent.heroSubHeading,
    url: `https://apollopurposeportal.com/${event.pageUrl}`,
    image: "",
  };

  let formattedHeroHeading = pageContent.heroHeading.replace(/\\n/g, "<br /><span>").replace(/\n/g, "<br /><span>");
  if (formattedHeroHeading.includes("<br /><span>")) {
    formattedHeroHeading += "</span>";
  }

  const user = {
    name: "Lauren Olivier",
    accountType: "EXECUTIVE",
    avatarUrl: "https://i.ibb.co/xqnrW3cK/woman-pfp.jpg",
    hasNotifications: true,
  };

  return (
    <>
      <Head head={head} noTwitterCard={true} />
      <div className={`ed-wrapper ${textClass}`}>
        <div className="ed-main">
          <Navbar user={user} currentPage="portal" />

          <BoardMatchmakingPortalPage />

          {pageContent.footerImages && pageContent.footerImages.length ? (
            <div className="auto__container">
              <div className="footer-image-outer">
                <div className="footer-image">
                  <img className="img-fluid" src={pageContent.footerImages[0]} />
                </div>
              </div>
            </div>
          ) : null}
          <div className="footer-spacer"></div>

          <ApolloQuote />

          {!pageContent.hideLandmarkFooter ? (
            <div className="ed-footer">
              <Footer alternate={true} />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
